import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Divider, IconButton, Modal, Skeleton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import AttachmentApi, { AttachmentAssetResponse } from '../../api/AttachmentApi';
import { AttachmentAsset } from '../../models/standard/AttachmentAsset';
import { usePlan } from '../../provider/PlanProvider';
import { useUserDataContext } from '../../provider/UserDataProvider';
import { getPrimaryDate, sortByAttachmentDateDesc } from '../../utils/attachmentUtils';
import { PLATFORM_URL } from '../../utils/constants';

const useStyles = makeStyles((theme) => ({
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 900,
    height: 600,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 15,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2.5, 3),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
  },
  modalTitle: {
    fontSize: '1.3rem !important',
    fontWeight: 500,
  },
  gridContainer: {
    flex: 1,
    overflowY: 'auto',
    marginTop: theme.spacing(2),
    paddingRight: theme.spacing(1),
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: theme.spacing(3),
  },
  card: {
    background: '#fff',
    borderRadius: 12,
    padding: theme.spacing(1.5),
    cursor: 'pointer',
    transition: '0.2s',
    border: '2px solid transparent',
    '&:hover': {
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
  },
  cardSelected: {
    border: '2px solid #009fe3',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
  },
  thumb: {
    width: '100%',
    height: 160,
    borderRadius: 10,
    objectFit: 'cover',
    marginBottom: theme.spacing(1),
  },
  filename: {
    fontWeight: 600,
    color: '#333',
    fontSize: '0.95rem',
  },
  meta: {
    fontSize: '0.85rem !important',
    color: '#666',
  },
  description: {
    color: '#666',
    fontSize: '0.9rem !important',
  },
  divider: {
    marginLeft: '-24px !important',
    marginRight: '-24px !important',
    backgroundColor: '#d3d3d3',
    height: '1px',
  },
  footer: {
    display: 'flex',
    justifyContent: 'flex-end',
    paddingTop: theme.spacing(2),
  },
  actionBtn: {
    backgroundColor: '#009fe3 !important',
    color: '#fff !important',
    padding: theme.spacing(1, 3),
    fontSize: '0.9rem !important',
    borderRadius: 20,
    textTransform: 'uppercase',
    '&.Mui-disabled': {
      backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
      color: 'rgba(0, 0, 0, 0.26) !important',
    },
  },
}));

const GRID_COLUMNS = 3;
const ESTIMATED_ROW_HEIGHT = 260;

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (asset: AttachmentAsset) => void;
}

const AttachmentPanoramaModal: React.FC<Props> = ({ isOpen, onClose, onSelect }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { currentUserData } = useUserDataContext();
  const { planModel } = usePlan();

  const [attachments, setAttachments] = useState<AttachmentAsset[]>([]);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const token = currentUserData?.authentificationtoken;
  const hasMore = totalCount !== null ? attachments.length < totalCount : false;
  const isLoadingMore = loading && attachments.length > 0;

  const resetState = (): void => {
    setAttachments([]);
    setTotalCount(null);
    setSelected(null);
    setError(null);
    setLoading(false);
  };

  const fetchAttachments = async (offset: number): Promise<void> => {
    if (!token || !planModel || loading) {
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const res: AttachmentAssetResponse = await AttachmentApi.getAttachmentsPage(
        'panorama',
        token,
        planModel,
        offset
      );

      setAttachments((prev) => {
        const merged = offset === 0 ? res.data.slice() : [...prev, ...res.data];
        merged.sort(sortByAttachmentDateDesc);
        return merged;
      });

      setTotalCount(res.totalCount);
    } catch (e: unknown) {
      const message = e instanceof Error ? e.message : 'Unknown error';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen || !token || !planModel) {
      return;
    }

    resetState();
    void fetchAttachments(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, token, planModel]);

  useEffect(() => {
    if (!isOpen) {
      resetState();
    }
  }, [isOpen]);

  const handleCardClick = (id: number): void => {
    setSelected(id);
  };

  const handleAttach = (): void => {
    if (selected !== null) {
      const selectedAttachment = attachments.find((a) => a.id === selected);
      if (selectedAttachment) {
        onSelect(selectedAttachment);
      }
      onClose();
    }
  };

  const renderAttachmentCard = (attachment: AttachmentAsset): JSX.Element => {
    const filename = attachment.fileName || '';
    const hasFilename = filename !== '';

    const { date, fromShooting } = getPrimaryDate(attachment);
    const dateLabel = fromShooting ? 'Aufnahmedatum' : 'Erstelldatum';
    const dateText = date
      ? `${date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
        })} ${date.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}`
      : '-';

    const description = attachment.description || '';
    const imgSrc = `${PLATFORM_URL}${attachment.thumbnailUrl}`;
    const isSelected = selected === attachment.id;
    const altText = filename || `Panorama #${attachment.id}`;

    return (
      <Box key={attachment.id} className={`${classes.card} ${isSelected ? classes.cardSelected : ''}`} onClick={() => handleCardClick(attachment.id)}>
        <img src={imgSrc} alt={altText} className={classes.thumb} />

        {hasFilename && <Typography className={classes.filename}>{filename}</Typography>}

        <Typography className={classes.meta}>
          {dateLabel}: {dateText}
        </Typography>

        {description && <Typography className={classes.description}>{description}</Typography>}
      </Box>
    );
  };

  let remainingSpacerHeight = 0;
  let loadedContentHeight = 0;
  if (totalCount !== null && totalCount > 0) {
    const totalRows = Math.ceil(totalCount / GRID_COLUMNS);
    const loadedRows = Math.ceil(attachments.length / GRID_COLUMNS);
    const remainingRows = Math.max(totalRows - loadedRows, 0);

    loadedContentHeight = loadedRows * ESTIMATED_ROW_HEIGHT;
    remainingSpacerHeight = remainingRows * ESTIMATED_ROW_HEIGHT;
  }

  const handleScroll = (event: React.UIEvent<HTMLDivElement>): void => {
    if (!hasMore || loading) {
      return;
    }

    const { scrollTop, clientHeight } = event.currentTarget;
    const threshold = 100;

    if (loadedContentHeight > 0 && scrollTop + clientHeight >= loadedContentHeight - threshold) {
      void fetchAttachments(attachments.length);
    }
  };

  const shouldShowGrid = !loading && !error && attachments.length > 0;

  // Determine context: Project (Event) vs Showroom (Level)
  const isProjectContext = planModel && planModel.kojeId !== -1 && planModel.eventId !== -1;

  return (
    <Modal open={isOpen} onClose={onClose} BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.5)' } }}>
      <Box className={classes.modalBox}>
        {/* Header */}
        <div className={classes.modalHeader}>
          <Typography className={classes.modalTitle} style={{ color: '#464646' }}>
            {formatMessage({
              id: isProjectContext ? 'PanoramaSelect.titleEvent' : 'PanoramaSelect.titleLevel',
              defaultMessage: isProjectContext 
                ? 'Panorama aus Ereignis wählen' 
                : 'Panorama aus Ebene wählen',
            })}
          </Typography>

          <IconButton onClick={onClose}>
            <CloseIcon style={{ color: '#464646' }} />
          </IconButton>
        </div>

        <Divider className={classes.divider} />

        {/* Scrollable Grid */}
        <div className={classes.gridContainer} onScroll={handleScroll}>
          {loading && attachments.length === 0 && (
            <Typography className={classes.meta}>
              {formatMessage({
                id: 'PanoramaSelect.loading',
                defaultMessage: 'Panoramen werden geladen…',
              })}
            </Typography>
          )}

          {error && (
            <Typography color="error" className={classes.meta}>
              {formatMessage(
                {
                  id: 'PanoramaSelect.error',
                  defaultMessage: 'Fehler beim Laden der Panoramen: {message}',
                },
                { message: error },
              )}
            </Typography>
          )}

          {!loading && !error && attachments.length === 0 && (
            <Typography className={classes.meta}>
              {formatMessage({
                id: 'PanoramaSelect.empty',
                defaultMessage: 'Keine Panoramen gefunden.',
              })}
            </Typography>
          )}

          {(shouldShowGrid || isLoadingMore) && (
            <div className={classes.grid}>
              {attachments.map((attachment) => renderAttachmentCard(attachment))}

              {isLoadingMore &&
                Array.from({ length: 3 }).map((_, index) => (
                  <Box key={`skeleton-${index}`} className={classes.card}>
                    <Skeleton variant="rectangular" className={classes.thumb} />
                    <Skeleton width="60%" />
                    <Skeleton width="40%" />
                  </Box>
                ))}

              {remainingSpacerHeight > 0 && <Box style={{ height: remainingSpacerHeight }} />}
            </div>
          )}
        </div>

        {/* Footer Button */}
        <div className={classes.footer}>
          <Button disabled={selected === null} className={classes.actionBtn} onClick={handleAttach}>
            {formatMessage({
              id: 'PanoramaSelect.attach',
              defaultMessage: 'ATTACHMENT HINZUFÜGEN',
            })}
          </Button>
        </div>
      </Box>
    </Modal>
  );
};

export default AttachmentPanoramaModal;